import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { RISK_COLORS, RISK_CODE_TO_LEVEL } from '../../types';
import type { RiskLevel } from '../../types';

export type ViewMode = 'risk' | 'anomaly';

interface CountryRiskLayerProps {
  cells: number[][]; // [[lat, lon, prob, risk_code, percentile?], ...]
  onCellClick?: (lat: number, lon: number, prob: number, risk: RiskLevel, percentile?: number) => void;
  visibleLevels?: Set<RiskLevel>;
  viewMode?: ViewMode;
  /** Show the per-cell number in the hover tooltip. Off for share-banded maps
   * (decision D8: the band is the message, the number is not a probability). */
  showProbability?: boolean;
}

const ANOMALY_PERCENTILE = 95;
const RING_ZOOM_THRESHOLD = 9;
const ALL_LEVELS: RiskLevel[] = ['Low', 'Moderate', 'High', 'Extreme'];
const LEVEL_PT: Record<RiskLevel, string> = {
  Low: 'Baixo',
  Moderate: 'Moderado',
  High: 'Alto',
  Extreme: 'Extremo',
};

// Anomaly view bins percentile into 4 colors that map cleanly onto the
// existing 4 risk-level layer groups so the rest of the layer-toggle
// machinery (groups, visibleLevels, anomaly rings) still applies. Bins:
//   <50   typical                  → "Low"      (gray-blue)
//   50-74 slightly elevated        → "Moderate" (yellow)
//   75-94 notable                  → "High"     (orange)
//   >=95  anomaly (top 5% for mo)  → "Extreme"  (red)
function percentileToBin(percentile: number | null | undefined): RiskLevel {
  if (percentile == null) return 'Low';
  if (percentile >= ANOMALY_PERCENTILE) return 'Extreme';
  if (percentile >= 75) return 'High';
  if (percentile >= 50) return 'Moderate';
  return 'Low';
}

export default function CountryRiskLayer({
  cells,
  onCellClick,
  visibleLevels,
  viewMode = 'risk',
  showProbability = true,
}: CountryRiskLayerProps) {
  const map = useMap();
  // Per-level layer groups so toggling visibility is O(1): addLayer/removeLayer
  // a whole group at once, no marker rebuild.
  const groupsRef = useRef<Record<RiskLevel, L.LayerGroup> | null>(null);
  const anomalyMarkersRef = useRef<L.CircleMarker[]>([]);
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);

  // Build markers ONCE per cells/onCellClick/viewMode change. Toggling levels
  // or zoom never reaches this path; viewMode does because the per-cell color,
  // opacity, and group-assignment all depend on it.
  useEffect(() => {
    if (!cells || cells.length === 0) return;

    if (groupsRef.current) {
      for (const level of ALL_LEVELS) map.removeLayer(groupsRef.current[level]);
    }
    anomalyMarkersRef.current = [];

    const renderer = L.canvas({ padding: 0.5 });
    const groups: Record<RiskLevel, L.LayerGroup> = {
      Low: L.layerGroup(),
      Moderate: L.layerGroup(),
      High: L.layerGroup(),
      Extreme: L.layerGroup(),
    };
    const showAnomalyRings = map.getZoom() >= RING_ZOOM_THRESHOLD;

    for (const cell of cells) {
      const [lat, lon, prob, riskCode, percentile] = cell;
      const riskLevel = RISK_CODE_TO_LEVEL[riskCode] || 'Low';
      const isAnomaly = percentile != null && percentile >= ANOMALY_PERCENTILE;
      // In anomaly view: bin by percentile into the 4 risk-level groups so
      // toggle visibility and color reuse the same machinery.
      const displayLevel: RiskLevel = viewMode === 'anomaly'
        ? percentileToBin(percentile)
        : riskLevel;
      const color = RISK_COLORS[displayLevel];
      // Anomaly rings only make sense on the risk view (where they call out
      // top-percentile cells over a shifting risk colormap). Anomaly view
      // already encodes percentile in the fill, so suppress the ring.
      const showRing = viewMode === 'risk' && isAnomaly && showAnomalyRings;
      // In anomaly mode, fill opacity reflects how far above baseline the
      // cell is, not the raw probability, keeps "typical" cells faint.
      const fillOpacity = viewMode === 'anomaly'
        ? (percentile != null ? 0.2 + (percentile / 100) * 0.55 : 0.2)
        : 0.3 + prob * 0.35;

      const marker = L.circleMarker([lat, lon], {
        renderer,
        radius: showRing ? 5 : 3,
        fillColor: color,
        fillOpacity,
        stroke: showRing,
        color: '#ffffff',
        weight: showRing ? 2 : 0,
      });

      if (onCellClick) {
        marker.on('click', () => {
          onCellClick(lat, lon, prob, riskLevel, percentile);
        });
      }

      const tooltipText = !showProbability
        ? LEVEL_PT[riskLevel]
        : percentile != null
          ? `${LEVEL_PT[riskLevel]}: ${(prob * 100).toFixed(1)}% (p${percentile})`
          : `${LEVEL_PT[riskLevel]}: ${(prob * 100).toFixed(1)}%`;
      marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -5] });

      groups[displayLevel].addLayer(marker);
      if (isAnomaly) anomalyMarkersRef.current.push(marker);
    }

    // Add only currently-visible groups; the visibility effect below reconciles
    // on the next tick if `visibleLevels` is set.
    for (const level of ALL_LEVELS) {
      if (!visibleLevels || visibleLevels.has(level)) {
        groups[level].addTo(map);
      }
    }
    groupsRef.current = groups;

    return () => {
      if (groupsRef.current) {
        for (const level of ALL_LEVELS) map.removeLayer(groupsRef.current[level]);
        groupsRef.current = null;
      }
      anomalyMarkersRef.current = [];
    };
  }, [cells, map, onCellClick, viewMode, showProbability]);

  // Toggle visibility per level: single addLayer/removeLayer per level, no
  // marker reallocation regardless of cell count.
  useEffect(() => {
    if (!groupsRef.current) return;
    for (const level of ALL_LEVELS) {
      const group = groupsRef.current[level];
      const shouldShow = !visibleLevels || visibleLevels.has(level);
      const isShown = map.hasLayer(group);
      if (shouldShow && !isShown) map.addLayer(group);
      else if (!shouldShow && isShown) map.removeLayer(group);
    }
  }, [visibleLevels, map]);

  // Toggle anomaly-ring style in place when zoom crosses RING_ZOOM_THRESHOLD.
  useEffect(() => {
    if (anomalyMarkersRef.current.length === 0) return;
    const showRing = zoom >= RING_ZOOM_THRESHOLD;
    for (const marker of anomalyMarkersRef.current) {
      marker.setStyle({
        radius: showRing ? 5 : 3,
        stroke: showRing,
        weight: showRing ? 2 : 0,
      });
    }
  }, [zoom]);

  return null;
}
